import { useQuery } from '@tanstack/react-query';
import { administrationClient, unwrap, createResource } from '@/shared/api-client';

export interface Role {
  id: string;
  name: string;
  isSystem: boolean;
  permissions: string[];
  createdAt: string;
  updatedAt: string;
}

export interface RoleListResponse {
  items: Role[];
}

export interface CreateRoleInput {
  name: string;
  permissions: string[];
}

export interface UpdateRoleInput {
  name?: string;
  permissions?: string[];
  confirmUnresolvedVenueAccess?: boolean;
}

/**
 * The `GET /api/v1/franchise-administration/permissions` catalogue, grouped area-first then module then action
 * (ADR 0032). `area` is the PermissionArea wire value (`franchise_administration`
 * or `venue_workspace`).
 */
export interface PermissionCatalogue {
  areas: {
    area: string;
    modules: {
      module: string;
      actions: { action: string; permissions: { code: string }[] }[];
    }[];
  }[];
}

export const listRoles = (): Promise<RoleListResponse> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/roles'),
  ) as Promise<RoleListResponse>;

export const getRole = (id: string): Promise<Role> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
    }),
  ) as Promise<Role>;

export const createRole = (input: CreateRoleInput): Promise<Role> =>
  unwrap(
    administrationClient.POST('/api/v1/franchise-administration/roles', { body: input }),
  ) as Promise<Role>;

export const updateRole = (id: string, input: UpdateRoleInput): Promise<Role> =>
  unwrap(
    administrationClient.PATCH('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
      body: input,
    }),
  ) as Promise<Role>;

export const deleteRole = (id: string): Promise<void> =>
  unwrap(
    administrationClient.DELETE('/api/v1/franchise-administration/roles/{id}', {
      params: { path: { id } },
    }),
  );

export const getPermissionCatalogue = (): Promise<PermissionCatalogue> =>
  unwrap(
    administrationClient.GET('/api/v1/franchise-administration/permissions'),
  ) as Promise<PermissionCatalogue>;

/** Roles routed through the shared resource factory (ADR 0012); the typed fetch
 *  closures above stay per-module, the react-query ribbon is the factory's. */
const roles = createResource({
  key: ['roles'],
  list: listRoles,
  get: getRole,
  create: createRole,
  update: ({ id, input }: { id: string; input: UpdateRoleInput }) => updateRole(id, input),
  remove: deleteRole,
});

export const useRoles = roles.useList;
export const useRole = roles.useDetail;
export const useCreateRole = roles.useCreate;
export const useUpdateRole = roles.useUpdate;
export const useDeleteRole = roles.useRemove;

export function usePermissionCatalogue() {
  return useQuery({ queryKey: ['permissions'], queryFn: getPermissionCatalogue });
}
